import { DialogBody, DialogFooter } from '@civilgrid/ui/components';
import { useNavigate } from '@tanstack/react-router';
import { useSnapshot } from 'valtio';
import { captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { CIVIL_DRAW_ROUTE, type Project } from '~/common/types';
import { Dropdown } from '~/components2/Dropdown';
import Button from '~/components2/forms/Button';
import Icon from '~/components2/Icon';
import DeleteMapDialog from '../../../common/DeleteMapDialog';
import store from '../../../store';
import { ImportKmz } from './ImportKMZ';

const RESTRICTED_BROWSERS = ['Firefox'];

export default function Menu() {
  const { projectId, currentMapId, undoStack, redoStack, currentMap, project } = useSnapshot(store);
  const navigate = useNavigate();

  const checkBrowser = () => {
    const ua = navigator.userAgent;
    for (let i = 0; i < RESTRICTED_BROWSERS.length; i++) {
      const browser = RESTRICTED_BROWSERS[i];
      if (ua.indexOf(browser) !== -1) {
        return alert(`${browser} is currently not supported for printing.`);
      }
    }

    store.resetCurrentObjectId();
    store.printView = true;
    window.document.body.requestFullscreen();
    return;
  };

  if (currentMap.isLoading) {
    return null;
  }

  return (
    <div className="flex items-center rounded-sm ">
      <Dropdown
        align="start"
        triggerElement={
          <Button icon="menu" size="icon" aria-label="CivilDraw menu" data-testid="civildraw-menu-trigger" />
        }
        items={[
          {
            type: 'simple',
            label: 'Undo',
            onClick: async () => {
              await store.undo();
              const analyticsProject = project as unknown as Project;
              captureTypedPosthogEvent(POSTHOG_EVENTS.civildrawActionUndone, {
                project_id: analyticsProject.id,
                organization_id: analyticsProject.organization || undefined,
                placement: 'menu',
                ...(currentMapId && { map_id: currentMapId }),
              });
            },
            isDisabled: undoStack.length === 0,
          },
          {
            type: 'simple',
            label: 'Redo',
            onClick: async () => {
              await store.redo();
              const analyticsProject = project as unknown as Project;
              captureTypedPosthogEvent(POSTHOG_EVENTS.civildrawActionRedone, {
                project_id: analyticsProject.id,
                organization_id: analyticsProject.organization || undefined,
                placement: 'menu',
                ...(currentMapId && { map_id: currentMapId }),
              });
            },
            isDisabled: redoStack.length === 0,
          },
          {
            type: 'subMenu',
            label: 'Units',
            items: [
              {
                type: 'simple',
                label: (
                  <span className="flex w-full items-center justify-between">
                    Imperial
                    {currentMap.unitSystem === 'imperial' && <Icon icon="check" />}
                  </span>
                ),
                onClick: () => {
                  store.updateMap({
                    unitSystem: 'imperial',
                  });
                  const analyticsProject = project as unknown as Project;
                  captureTypedPosthogEvent(POSTHOG_EVENTS.civildrawUnitsToggled, {
                    project_id: analyticsProject.id,
                    organization_id: analyticsProject.organization || undefined,
                    measurement_unit: 'imperial',
                    ...(currentMapId && { map_id: currentMapId }),
                  });
                },
              },
              {
                type: 'simple',
                label: (
                  <span className="flex w-full items-center justify-between">
                    Metric
                    {currentMap.unitSystem === 'metric' && <Icon icon="check" />}
                  </span>
                ),
                onClick: () => {
                  store.updateMap({
                    unitSystem: 'metric',
                  });
                  const analyticsProject = project as unknown as Project;
                  captureTypedPosthogEvent(POSTHOG_EVENTS.civildrawUnitsToggled, {
                    project_id: analyticsProject.id,
                    organization_id: analyticsProject.organization || undefined,
                    measurement_unit: 'metric',
                    ...(currentMapId && { map_id: currentMapId }),
                  });
                },
              },
            ],
          },
          {
            type: 'separator',
          },
          {
            type: 'subMenu',
            label: 'Import',
            items: [
              {
                type: 'dialog',
                label: 'Import KMZ/KML',
                dialogTitle: 'Import KMZ/KML',
                dialogContent: (closeDialog) => <ImportKmz closeDialog={closeDialog} />,
              },
            ],
          },
          {
            type: 'dialog',
            label: 'Duplicate',
            dialogTitle: 'Duplicate This Map?',
            dialogContent: (closeDialog) => (
              <>
                <DialogBody className="text-sm">
                  Are you sure you want to duplicate <strong>{currentMap.name}</strong>?
                </DialogBody>
                <DialogFooter>
                  <Button alternate onClick={closeDialog} data-testid="civildraw-duplicate-map-cancel">
                    Cancel
                  </Button>
                  <Button
                    data-testid="civildraw-duplicate-map-confirm"
                    onClick={async () => {
                      const duplicateId = await store.duplicateMap(currentMap.id);
                      navigate({
                        to: `/projects/${projectId}/${CIVIL_DRAW_ROUTE}/${duplicateId}`,
                      });

                      const analyticsProject = project as unknown as Project;
                      captureTypedPosthogEvent(POSTHOG_EVENTS.civildrawMapDuplicated, {
                        project_id: analyticsProject.id,
                        organization_id: analyticsProject.organization || undefined,
                        map_id: currentMap.id,
                      });
                    }}
                  >
                    Duplicate
                  </Button>
                </DialogFooter>
              </>
            ),
          },
          {
            type: 'separator',
          },
          {
            type: 'simple',
            label: 'Print Map',
            onClick: () => {
              checkBrowser();
            },
          },
          {
            type: 'separator',
          },
          {
            type: 'dialog',
            label: 'Delete Map',
            dialogTitle: 'Delete Map?',
            dialogContent: (closeDialog) => <DeleteMapDialog map={currentMap} closeDialog={closeDialog} />,
          },
        ]}
      />
    </div>
  );
}
